import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const KEY_STORE = "apex_admin_key";
const GOLD = "#C5A059";

// Panel interno del estudio. Lo usa la dueña desde el teléfono, de pie y con
// gente delante, así que todo se resuelve en la misma pantalla: la lista del
// día, y encima de cada clase y de cada persona, lo que hay que poder hacerle.

const DIAS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

const fechaLarga = (iso) => {
  const d = new Date(iso + "T12:00:00");
  return `${DIAS[d.getDay()]} ${d.getDate()} de ${MESES[d.getMonth()]}`;
};
const hora12 = (hhmm) => {
  const [h, m] = String(hhmm).split(":").map(Number);
  const ap = h < 12 ? "AM" : "PM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ap}`;
};
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const mensajeDeError = (e) => e.response?.data?.detail || "No se pudo. Intenta de nuevo.";

const borde = { borderColor: `${GOLD}33` };
const Boton = ({ children, tono = "normal", ...props }) => {
  const estilos = {
    normal: { border: `1px solid ${GOLD}55`, color: "#fff", background: "transparent" },
    oro: { border: `1px solid ${GOLD}`, color: "#0C0C0C", background: GOLD, fontWeight: 700 },
    rojo: { border: "1px solid #7f1d1d", color: "#fca5a5", background: "transparent" },
  }[tono];
  return (
    <button
      {...props}
      className={`px-3 py-1.5 text-xs uppercase tracking-wider disabled:opacity-40 ${props.className || ""}`}
      style={{ ...estilos, ...(props.style || {}) }}
    >
      {children}
    </button>
  );
};

const Campo = ({ etiqueta, ...props }) => (
  <label className="block">
    <span className="block mb-1 text-[10px] uppercase tracking-[0.2em] text-white/40">{etiqueta}</span>
    <input {...props} className="w-full h-10 bg-black/60 border px-3 text-white outline-none text-sm" style={borde} />
  </label>
);

/** Ventana flotante para las acciones que piden confirmar o rellenar algo. */
const Ventana = ({ titulo, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/80 p-0 sm:p-6" onClick={onClose}>
    <div
      onClick={(e) => e.stopPropagation()}
      className="w-full sm:max-w-lg max-h-[88vh] overflow-y-auto border p-6"
      style={{ ...borde, background: "#111" }}
    >
      <div className="flex items-start justify-between gap-4">
        <h3 className="text-base font-bold text-white">{titulo}</h3>
        <button onClick={onClose} className="text-white/40 hover:text-white text-xl leading-none">
          ×
        </button>
      </div>
      <div className="mt-5">{children}</div>
    </div>
  </div>
);

export default function Admin() {
  const [key, setKey] = useState(() => sessionStorage.getItem(KEY_STORE) || "");
  const [authed, setAuthed] = useState(false);
  const [pass, setPass] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");
  const [q, setQ] = useState("");
  const [soloConGente, setSoloConGente] = useState(false);
  const [pestana, setPestana] = useState("agenda");
  const [ventana, setVentana] = useState(null); // {tipo, ...datos}

  const api = useCallback(
    (metodo, ruta, cuerpo) =>
      axios({
        method: metodo,
        url: `${API}${ruta}`,
        data: cuerpo,
        headers: { "X-Admin-Key": key || pass },
      }).then((r) => r.data),
    [key, pass]
  );

  const cargar = useCallback(
    async (k) => {
      setLoading(true);
      setError("");
      try {
        const r = await axios.get(`${API}/admin/overview`, { params: { days: 21 }, headers: { "X-Admin-Key": k } });
        setData(r.data);
        setAuthed(true);
        sessionStorage.setItem(KEY_STORE, k);
        setKey(k);
      } catch (e) {
        if (e.response?.status === 401) {
          setError("Clave incorrecta.");
          setAuthed(false);
          sessionStorage.removeItem(KEY_STORE);
        } else setError(mensajeDeError(e));
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (key) cargar(key);
  }, [key, cargar]);

  // El aviso de "listo" se va solo; no hace falta cerrarlo.
  useEffect(() => {
    if (!aviso) return;
    const t = setTimeout(() => setAviso(""), 4000);
    return () => clearTimeout(t);
  }, [aviso]);

  const salir = () => {
    sessionStorage.removeItem(KEY_STORE);
    setKey("");
    setAuthed(false);
    setData(null);
    setPass("");
  };

  const hacer = async (fn, mensaje) => {
    setError("");
    try {
      await fn();
      setVentana(null);
      if (mensaje) setAviso(mensaje);
      await cargar(key);
      return true;
    } catch (e) {
      setError(mensajeDeError(e));
      return false;
    }
  };

  const hoy = todayISO();

  const clases = useMemo(() => {
    if (!data) return [];
    let list = data.classes;
    if (soloConGente) list = list.filter((c) => c.riders.length > 0);
    const term = q.trim().toLowerCase();
    if (term) {
      list = list
        .map((c) => ({ ...c, riders: c.riders.filter((r) => r.name.toLowerCase().includes(term) || r.email.toLowerCase().includes(term)) }))
        .filter((c) => c.riders.length > 0);
    }
    return list;
  }, [data, q, soloConGente]);

  const porFecha = useMemo(() => {
    const g = {};
    clases.forEach((c) => (g[c.date] = g[c.date] || []).push(c));
    return g;
  }, [clases]);
  const fechas = Object.keys(porFecha).sort();

  const ridersHoy = useMemo(
    () => (data ? data.classes.filter((c) => c.date === hoy).reduce((n, c) => n + c.riders.length, 0) : 0),
    [data, hoy]
  );

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "#0C0C0C" }}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            cargar(pass);
          }}
          className="w-full max-w-sm border p-8"
          style={{ borderColor: `${GOLD}44`, background: "#111" }}
          data-testid="admin-login"
        >
          <p className="text-xs tracking-[0.35em] uppercase" style={{ color: GOLD }}>
            Apex Ride
          </p>
          <h1 className="mt-2 text-2xl font-bold text-white">Panel del estudio</h1>
          <p className="mt-1 text-sm text-white/50">Acceso interno — reservas, horario y clientes</p>
          <input
            autoFocus
            type="password"
            value={pass}
            onChange={(e) => setPass(e.target.value)}
            placeholder="Clave de acceso"
            data-testid="admin-pass"
            className="mt-6 w-full h-12 bg-black/60 border px-4 text-white outline-none"
            style={{ borderColor: `${GOLD}55` }}
          />
          {error && <p className="mt-2 text-sm text-red-400">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-enter"
            className="mt-4 w-full py-3 font-bold uppercase tracking-widest text-black disabled:opacity-60"
            style={{ background: GOLD }}
          >
            {loading ? "…" : "Entrar"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" style={{ background: "#0C0C0C" }}>
      <header
        className="sticky top-0 z-20 border-b px-5 py-4"
        style={{ background: "#0C0C0Cee", backdropFilter: "blur(8px)", borderColor: `${GOLD}33` }}
      >
        <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-[10px] tracking-[0.35em] uppercase" style={{ color: GOLD }}>
              Apex Ride
            </p>
            <h1 className="text-xl font-bold text-white">Panel del estudio</h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold px-3 py-1.5 text-black" style={{ background: GOLD }}>
              {ridersHoy} rider{ridersHoy === 1 ? "" : "s"} hoy
            </span>
            <Boton onClick={() => cargar(key)}>{loading ? "Actualizando…" : "Actualizar"}</Boton>
            <button onClick={salir} className="text-sm px-3 py-1.5 text-white/50 hover:text-white">
              Salir
            </button>
          </div>
        </div>

        <div className="max-w-5xl mx-auto mt-4 flex gap-5">
          {[
            ["agenda", "Agenda"],
            ["clientes", "Clientes"],
            ["dinero", "Dinero"],
            ["cupones", "Códigos"],
          ].map(([id, texto]) => (
            <button
              key={id}
              onClick={() => setPestana(id)}
              className="pb-2 text-sm uppercase tracking-[0.2em]"
              style={{
                color: pestana === id ? GOLD : "#ffffff66",
                borderBottom: `2px solid ${pestana === id ? GOLD : "transparent"}`,
              }}
              data-testid={`admin-tab-${id}`}
            >
              {texto}
            </button>
          ))}
        </div>
      </header>

      {(error || aviso) && (
        <div className="max-w-5xl mx-auto px-5 pt-4">
          {error && <p className="border border-red-900/60 bg-red-950/30 px-4 py-2.5 text-sm text-red-300">{error}</p>}
          {aviso && (
            <p className="border px-4 py-2.5 text-sm" style={{ borderColor: `${GOLD}55`, color: GOLD }}>
              {aviso}
            </p>
          )}
        </div>
      )}

      {/* Pagos que se quedaron sin sitio: lo primero que debe ver al entrar. */}
      {data?.revisar?.length > 0 && (
        <div className="max-w-5xl mx-auto px-5 pt-4" data-testid="admin-review">
          <div className="border border-red-900/60 bg-red-950/30 px-5 py-4">
            <p className="text-sm font-bold text-red-300">
              {data.revisar.length} pago{data.revisar.length === 1 ? "" : "s"} sin lugar asignado
            </p>
            <p className="mt-1 text-xs text-red-200/70">
              Pagaron, pero su sitio se ocupó mientras pagaban. Llámalos para moverlos o devolverles el dinero.
            </p>
            <div className="mt-3 divide-y" style={{ borderColor: "#ffffff11" }}>
              {data.revisar.map((r) => (
                <div key={r.receipt} className="py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                  <span className="font-semibold text-white">{r.name}</span>
                  <a href={`mailto:${r.email}`} className="text-white/60 hover:text-white">
                    {r.email}
                  </a>
                  {r.phone && (
                    <a href={`tel:${r.phone}`} className="text-white/60 hover:text-white">
                      {r.phone}
                    </a>
                  )}
                  <span className="text-white/70">
                    {fechaLarga(r.class_date)} · {hora12(r.class_time)}
                  </span>
                  <span className="text-white/50">${Number(r.total).toFixed(2)}</span>
                  <span className="ml-auto text-white/30 text-xs">{r.receipt}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {pestana === "agenda" ? (
        <div className="max-w-5xl mx-auto px-5 py-6">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre o correo…"
              data-testid="admin-search"
              className="h-11 flex-1 min-w-[200px] bg-black/60 border px-4 text-white outline-none"
              style={borde}
            />
            <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer select-none">
              <input type="checkbox" checked={soloConGente} onChange={(e) => setSoloConGente(e.target.checked)} className="accent-[#C5A059]" />
              Solo con reservas
            </label>
            <Boton tono="oro" onClick={() => setVentana({ tipo: "nueva-clase" })} data-testid="admin-new-class">
              + Clase
            </Boton>
          </div>

          {fechas.length === 0 ? (
            <p className="py-16 text-center text-white/50">
              {q || soloConGente ? "Nada que mostrar con ese filtro." : "No hay clases publicadas."}
            </p>
          ) : (
            fechas.map((f) => (
              <div key={f} className="mb-8">
                <h2 className="text-sm font-bold uppercase tracking-[0.2em] mb-3" style={{ color: f === hoy ? GOLD : "#ffffff66" }}>
                  {f === hoy ? "Hoy · " : ""}
                  {fechaLarga(f)}
                </h2>
                <div className="space-y-3">
                  {porFecha[f]
                    .sort((a, b) => a.time.localeCompare(b.time))
                    .map((c) => (
                      <ClaseCard
                        key={c.id}
                        c={c}
                        onReservar={() => setVentana({ tipo: "reservar", clase: c })}
                        onEditar={() => setVentana({ tipo: "editar-clase", clase: c })}
                        onCancelar={() => setVentana({ tipo: "cancelar-clase", clase: c })}
                        onMover={(r) => setVentana({ tipo: "mover", clase: c, rider: r })}
                        onBaja={(r) => setVentana({ tipo: "baja", clase: c, rider: r })}
                      />
                    ))}
                </div>
              </div>
            ))
          )}
        </div>
      ) : pestana === "clientes" ? (
        <Clientes api={api} paquetes={data?.packages || []} onHecho={(m) => setAviso(m)} onError={setError} />
      ) : pestana === "dinero" ? (
        <Dinero api={api} onError={setError} />
      ) : (
        <Cupones api={api} onHecho={(m) => setAviso(m)} onError={setError} />
      )}

      {ventana?.tipo === "nueva-clase" && (
        <Ventana titulo="Clase nueva" onClose={() => setVentana(null)}>
          <FormClase
            coaches={data?.coaches || []}
            onGuardar={(cuerpo) => hacer(() => api("post", "/admin/classes", cuerpo), "Clase creada.")}
          />
        </Ventana>
      )}

      {ventana?.tipo === "reservar" && (
        <Ventana
          titulo={`Reservar · ${hora12(ventana.clase.time)} ${fechaLarga(ventana.clase.date)}`}
          onClose={() => setVentana(null)}
        >
          <FormReservar
            clase={ventana.clase}
            onGuardar={async (cuerpo) => {
              let resumen = "";
              const ok = await hacer(async () => {
                const r = await api("post", "/admin/bookings", { class_id: ventana.clase.id, ...cuerpo });
                resumen = `Reservado · ${r.receipt_number}${r.correo_enviado ? " · recibo enviado" : ""}`;
              });
              if (ok && resumen) setAviso(resumen);
              return ok;
            }}
          />
        </Ventana>
      )}

      {ventana?.tipo === "editar-clase" && (
        <Ventana titulo={`${hora12(ventana.clase.time)} · ${fechaLarga(ventana.clase.date)}`} onClose={() => setVentana(null)}>
          <FormEditarClase
            clase={ventana.clase}
            coaches={data?.coaches || []}
            onGuardar={(cuerpo) => hacer(() => api("patch", `/admin/classes/${ventana.clase.id}`, cuerpo), "Clase actualizada.")}
          />
        </Ventana>
      )}

      {ventana?.tipo === "cancelar-clase" && (
        <Ventana titulo="Cancelar la clase" onClose={() => setVentana(null)}>
          <CancelarClase
            clase={ventana.clase}
            onConfirmar={(nota) =>
              hacer(
                () =>
                  api(
                    "delete",
                    `/admin/classes/${ventana.clase.id}?force=1${nota ? `&nota=${encodeURIComponent(nota)}` : ""}`
                  ),
                ventana.clase.riders.length
                  ? `Clase cancelada. Se avisó por correo a ${ventana.clase.riders.length} persona(s).`
                  : "Clase cancelada."
              )
            }
          />
        </Ventana>
      )}

      {ventana?.tipo === "baja" && (
        <Ventana titulo="Dar de baja la reserva" onClose={() => setVentana(null)}>
          <p className="text-sm text-white/70 leading-relaxed">
            Se libera {ventana.clase.kind === "floor" ? "el lugar" : `la bici ${ventana.rider.bikes.join(", ")}`} de{" "}
            <b className="text-white">{ventana.rider.name}</b> en la clase de {hora12(ventana.clase.time)}.
          </p>
          <p className="mt-2 text-xs text-white/40">
            El cobro no se toca: si hay que devolverle el dinero, hazlo por donde se cobró.
          </p>
          <div className="mt-6 flex gap-2 justify-end">
            <Boton onClick={() => setVentana(null)}>Volver</Boton>
            <Boton
              tono="rojo"
              onClick={() => hacer(() => api("post", `/admin/bookings/${ventana.rider.id}/cancel`), "Reserva dada de baja.")}
            >
              Dar de baja
            </Boton>
          </div>
        </Ventana>
      )}

      {ventana?.tipo === "mover" && (
        <Ventana titulo={`Mover a ${ventana.rider.name}`} onClose={() => setVentana(null)}>
          <MoverReserva
            clase={ventana.clase}
            rider={ventana.rider}
            clases={data?.classes || []}
            onMover={(claseId) =>
              hacer(
                () => api("post", `/admin/bookings/${ventana.rider.id}/move`, { class_id: claseId }),
                "Reserva movida. Le llegó un recibo nuevo."
              )
            }
          />
        </Ventana>
      )}
    </div>
  );
}

function ClaseCard({ c, onEditar, onCancelar, onMover, onBaja, onReservar }) {
  const llena = c.booked_count >= c.total_bikes;
  return (
    <div className="border p-4" style={{ ...borde, background: "#111" }} data-testid={`admin-class-${c.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-bold" style={{ color: GOLD }}>
            {hora12(c.time)}
          </span>
          <span className="text-white font-semibold">{c.name?.es || c.name?.en}</span>
          <span className="text-white/40 text-sm">· {c.instructor}</span>
          <span className="text-white/40 text-sm">· ${Number(c.price).toFixed(2)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="text-xs font-bold px-2.5 py-1"
            style={{
              background: llena ? "#7f1d1d" : `${GOLD}22`,
              color: llena ? "#fca5a5" : GOLD,
            }}
          >
            {c.booked_count}/{c.total_bikes} {c.kind === "floor" ? "lugares" : "bicis"}
          </span>
          <Boton tono="oro" onClick={onReservar} disabled={llena} data-testid={`admin-book-${c.id}`}>
            Reservar
          </Boton>
          <Boton onClick={onEditar} data-testid={`admin-edit-${c.id}`}>
            Editar
          </Boton>
          <Boton tono="rojo" onClick={onCancelar} data-testid={`admin-cancel-${c.id}`}>
            Cancelar
          </Boton>
        </div>
      </div>

      {c.riders.length > 0 && (
        <div className="mt-3 divide-y" style={{ borderColor: "#ffffff11" }}>
          {c.riders.map((r) => (
            <div key={r.receipt} className="py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" style={{ borderColor: "#ffffff11" }}>
              <span className="font-semibold text-white min-w-[130px]">{r.name}</span>
              <a href={`mailto:${r.email}`} className="text-white/60 hover:text-white">
                {r.email}
              </a>
              {r.phone && (
                <a href={`tel:${r.phone}`} className="text-white/60 hover:text-white">
                  {r.phone}
                </a>
              )}
              <span className="font-bold" style={{ color: GOLD }}>
                {c.kind === "floor" ? "Lugar" : "Bici"} {r.bikes.join(", ")}
              </span>
              <span className="text-white/50">${Number(r.total).toFixed(2)}</span>
              {r.coupon && (
                <span className="text-[10px] font-bold uppercase px-2 py-0.5" style={{ background: `${GOLD}22`, color: GOLD }}>
                  {r.coupon}
                </span>
              )}
              {r.free && <span className="text-[10px] font-bold uppercase px-2 py-0.5 bg-emerald-900/50 text-emerald-300">Clase gratis</span>}
              {r.membership && <span className="text-[10px] font-bold uppercase px-2 py-0.5 bg-sky-900/50 text-sky-300">Paquete</span>}
              <span className="ml-auto flex items-center gap-2">
                <Boton onClick={() => onMover(r)} data-testid={`admin-move-${r.receipt}`}>
                  Mover
                </Boton>
                <Boton tono="rojo" onClick={() => onBaja(r)} data-testid={`admin-drop-${r.receipt}`}>
                  Baja
                </Boton>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FormClase({ coaches, onGuardar }) {
  const [f, setF] = useState({ date: todayISO(), time: "18:00", kind: "ride", name_es: "", total_bikes: "", instructor: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const enviar = async (e) => {
    e.preventDefault();
    setBusy(true);
    await onGuardar({
      date: f.date,
      time: f.time,
      kind: f.kind,
      name_es: f.name_es || undefined,
      name_en: f.name_es || undefined,
      total_bikes: f.total_bikes || undefined,
      instructor: f.instructor || undefined,
    });
    setBusy(false);
  };

  return (
    <form onSubmit={enviar} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Campo etiqueta="Día" type="date" value={f.date} onChange={set("date")} required />
        <Campo etiqueta="Hora" type="time" value={f.time} onChange={set("time")} required />
      </div>
      <div>
        <span className="block mb-1 text-[10px] uppercase tracking-[0.2em] text-white/40">Tipo</span>
        <div className="flex gap-2">
          {[
            ["ride", "Spinning"],
            ["floor", "Suelo"],
          ].map(([id, texto]) => (
            <button
              key={id}
              type="button"
              onClick={() => setF({ ...f, kind: id })}
              className="flex-1 py-2.5 text-sm uppercase tracking-wider"
              style={
                f.kind === id
                  ? { background: GOLD, color: "#0C0C0C", fontWeight: 700 }
                  : { border: `1px solid ${GOLD}44`, color: "#fff" }
              }
            >
              {texto}
            </button>
          ))}
        </div>
      </div>
      <Campo etiqueta="Nombre (opcional)" value={f.name_es} onChange={set("name_es")} placeholder="Spinning" />
      <div className="grid grid-cols-2 gap-3">
        <Campo etiqueta="Lugares (opcional)" type="number" min="1" max="60" value={f.total_bikes} onChange={set("total_bikes")} placeholder={f.kind === "floor" ? "12" : "20"} />
        <label className="block">
          <span className="block mb-1 text-[10px] uppercase tracking-[0.2em] text-white/40">Coach (opcional)</span>
          <select value={f.instructor} onChange={set("instructor")} className="w-full h-10 bg-black/60 border px-3 text-white outline-none text-sm" style={borde}>
            <option value="">Por defecto</option>
            {coaches.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="text-xs text-white/40">El precio y la duración salen del tipo de clase. Solo puede haber una clase por hora.</p>
      <div className="flex justify-end gap-2 pt-2">
        <Boton tono="oro" type="submit" disabled={busy}>
          {busy ? "Creando…" : "Crear clase"}
        </Boton>
      </div>
    </form>
  );
}

function FormReservar({ clase, onGuardar }) {
  const esSuelo = clase.kind === "floor";
  const libres = clase.total_bikes - clase.booked_count;
  const [f, setF] = useState({ customer_name: "", email: "", spots: "1", bikes: "", cortesia: false });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const numeros = f.bikes
    .split(/[\s,]+/)
    .map((x) => parseInt(x, 10))
    .filter((x) => Number.isInteger(x));

  const cantidad = esSuelo || !numeros.length ? Number(f.spots) || 1 : numeros.length;
  const cobro = f.cortesia ? 0 : clase.price * cantidad;

  const enviar = async (e) => {
    e.preventDefault();
    setBusy(true);
    await onGuardar({
      customer_name: f.customer_name,
      email: f.email || undefined,
      bikes: !esSuelo && numeros.length ? numeros : undefined,
      spots: cantidad,
      cortesia: f.cortesia,
    });
    setBusy(false);
  };

  return (
    <form onSubmit={enviar} className="space-y-4">
      <p className="text-sm text-white/60">
        Quedan {libres} {esSuelo ? "lugares" : "bicis"} · ${Number(clase.price).toFixed(2)} cada{" "}
        {esSuelo ? "lugar" : "bici"}.
      </p>
      <Campo etiqueta="Nombre" value={f.customer_name} onChange={set("customer_name")} required autoFocus />
      <Campo etiqueta="Correo (opcional)" type="email" value={f.email} onChange={set("email")} placeholder="Para mandarle el recibo" />

      {esSuelo ? (
        <Campo etiqueta="Lugares" type="number" min="1" max={libres} value={f.spots} onChange={set("spots")} />
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <Campo etiqueta="Bicis (opcional)" value={f.bikes} onChange={set("bikes")} placeholder="7, 8" />
          <Campo etiqueta="Cuántas" type="number" min="1" max={libres} value={f.spots} onChange={set("spots")} disabled={numeros.length > 0} />
        </div>
      )}
      {!esSuelo && <p className="text-xs text-white/40">Si no pones números, le damos las primeras bicis libres.</p>}

      <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer select-none">
        <input type="checkbox" checked={f.cortesia} onChange={(e) => setF({ ...f, cortesia: e.target.checked })} className="accent-[#C5A059]" />
        Cortesía (no entró dinero)
      </label>

      <p className="text-sm" style={{ color: GOLD }}>
        Se apunta como cobrado: <b>${cobro.toFixed(2)}</b>
      </p>
      <p className="text-xs text-white/40">
        El cobro se hace en el estudio; esto solo aparta el lugar y lo deja en tus cuentas.
      </p>

      <div className="flex justify-end gap-2 pt-2">
        <Boton tono="oro" type="submit" disabled={busy} data-testid="admin-confirm-book">
          {busy ? "Reservando…" : "Reservar"}
        </Boton>
      </div>
    </form>
  );
}

function FormEditarClase({ clase, coaches, onGuardar }) {
  const [f, setF] = useState({
    total_bikes: String(clase.total_bikes),
    price: String(clase.price),
    instructor: clase.instructor,
    name_es: clase.name?.es || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const enviar = async (e) => {
    e.preventDefault();
    setBusy(true);
    await onGuardar({
      total_bikes: Number(f.total_bikes),
      price: Number(f.price),
      instructor: f.instructor,
      name_es: f.name_es,
      name_en: f.name_es,
    });
    setBusy(false);
  };

  return (
    <form onSubmit={enviar} className="space-y-4">
      <p className="text-sm text-white/60">
        {clase.booked_count} de {clase.total_bikes} {clase.kind === "floor" ? "lugares" : "bicis"} reservados.
      </p>
      <Campo etiqueta="Nombre" value={f.name_es} onChange={set("name_es")} />
      <div className="grid grid-cols-2 gap-3">
        <Campo etiqueta={clase.kind === "floor" ? "Lugares" : "Bicis"} type="number" min="1" max="60" value={f.total_bikes} onChange={set("total_bikes")} />
        <Campo etiqueta="Precio ($)" type="number" min="0" step="0.01" value={f.price} onChange={set("price")} />
      </div>
      <label className="block">
        <span className="block mb-1 text-[10px] uppercase tracking-[0.2em] text-white/40">Coach</span>
        <select value={f.instructor} onChange={set("instructor")} className="w-full h-10 bg-black/60 border px-3 text-white outline-none text-sm" style={borde}>
          {[...new Set([clase.instructor, ...coaches])].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </label>
      <p className="text-xs text-white/40">
        El precio nuevo vale para quien reserve a partir de ahora; a quien ya pagó no se le cobra ni se le devuelve nada.
      </p>
      <div className="flex justify-end gap-2 pt-2">
        <Boton tono="oro" type="submit" disabled={busy}>
          {busy ? "Guardando…" : "Guardar"}
        </Boton>
      </div>
    </form>
  );
}

function CancelarClase({ clase, onConfirmar }) {
  const [nota, setNota] = useState("");
  const [busy, setBusy] = useState(false);
  const n = clase.riders.length;

  return (
    <div>
      <p className="text-sm text-white/70 leading-relaxed">
        Se quita del horario la clase de <b className="text-white">{hora12(clase.time)}</b> del {fechaLarga(clase.date)}.
      </p>
      {n > 0 ? (
        <>
          <p className="mt-3 text-sm text-red-300">
            Hay {n} persona{n === 1 ? "" : "s"} apuntada{n === 1 ? "" : "s"}. Se les anula la reserva y les llega un correo avisando.
          </p>
          <div className="mt-3 border divide-y text-sm" style={{ ...borde, borderColor: "#ffffff11" }}>
            {clase.riders.map((r) => (
              <div key={r.receipt} className="px-3 py-2 flex flex-wrap gap-x-3 gap-y-1">
                <span className="text-white font-semibold">{r.name}</span>
                <a href={`mailto:${r.email}`} className="text-white/50">
                  {r.email}
                </a>
                {r.phone && (
                  <a href={`tel:${r.phone}`} className="text-white/50">
                    {r.phone}
                  </a>
                )}
                <span className="ml-auto text-white/50">${Number(r.total).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs text-white/40">Los reembolsos se hacen por donde se cobró; esto no toca el dinero.</p>
          <label className="block mt-4">
            <span className="block mb-1 text-[10px] uppercase tracking-[0.2em] text-white/40">Motivo para el correo (opcional)</span>
            <textarea
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              rows={2}
              maxLength={300}
              placeholder="La coach está enferma, nos vemos mañana."
              className="w-full bg-black/60 border px-3 py-2 text-white outline-none text-sm"
              style={borde}
            />
          </label>
        </>
      ) : (
        <p className="mt-3 text-sm text-white/40">No hay nadie apuntado.</p>
      )}
      <div className="mt-6 flex gap-2 justify-end">
        <Boton
          tono="rojo"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            await onConfirmar(nota);
            setBusy(false);
          }}
          data-testid="admin-confirm-cancel"
        >
          {busy ? "Cancelando…" : "Sí, cancelar la clase"}
        </Boton>
      </div>
    </div>
  );
}

function MoverReserva({ clase, rider, clases, onMover }) {
  const [busy, setBusy] = useState("");
  const n = rider.bikes.length || 1;

  // Solo las clases del mismo tipo donde quepan todos sus lugares: así el
  // precio no cambia y no hay que cobrar ni devolver nada.
  const opciones = useMemo(
    () =>
      clases
        .filter((c) => c.kind === clase.kind && c.id !== clase.id && c.total_bikes - c.booked_count >= n)
        .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time)),
    [clases, clase, n]
  );

  return (
    <div>
      <p className="text-sm text-white/60">
        Ahora está en {hora12(clase.time)} del {fechaLarga(clase.date)} · {clase.kind === "floor" ? "lugar" : "bici"}{" "}
        {rider.bikes.join(", ")}.
      </p>
      {opciones.length === 0 ? (
        <p className="mt-4 text-sm text-white/40">No hay otra clase del mismo tipo con espacio.</p>
      ) : (
        <div className="mt-4 max-h-[46vh] overflow-y-auto border divide-y" style={{ ...borde, borderColor: "#ffffff11" }}>
          {opciones.map((c) => (
            <button
              key={c.id}
              disabled={!!busy}
              onClick={async () => {
                setBusy(c.id);
                await onMover(c.id);
                setBusy("");
              }}
              className="w-full px-3 py-2.5 flex items-center gap-3 text-left text-sm hover:bg-white/5 disabled:opacity-40"
              data-testid={`admin-move-to-${c.id}`}
            >
              <span className="font-bold min-w-[72px]" style={{ color: GOLD }}>
                {hora12(c.time)}
              </span>
              <span className="text-white/70">{fechaLarga(c.date)}</span>
              <span className="ml-auto text-white/40 text-xs">
                {busy === c.id ? "Moviendo…" : `${c.total_bikes - c.booked_count} libres`}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const primerDiaDelMes = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

const dinero = (n) => `$${Number(n || 0).toFixed(2)}`;

/**
 * Lo que entró por cada disciplina. El estudio reparte un porcentaje con la
 * coach de aeróbicos, así que necesita ver spinning y suelo por separado, ya
 * cobrada la comisión de la pasarela.
 */
function Dinero({ api, onError }) {
  const [desde, setDesde] = useState(primerDiaDelMes);
  const [hasta, setHasta] = useState(todayISO);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setDatos(await api("get", `/admin/ingresos?desde=${desde}&hasta=${hasta}`));
    } catch (e) {
      onError(e.response?.data?.detail || "No se pudo calcular.");
    } finally {
      setCargando(false);
    }
  }, [api, desde, hasta, onError]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const bajarExcel = () => {
    if (!datos) return;
    const filas = [["Fecha de cobro", "Clase", "Disciplina", "Cliente", "Importe"]];
    for (const d of datos.detalle_clases)
      filas.push([d.cobrado_en.slice(0, 10), d.clase, d.disciplina, d.cliente, d.importe]);
    for (const v of datos.ventas_paquete)
      filas.push([
        v.fecha.slice(0, 10),
        `PAQUETE · ${v.paquete}${v.origen === "estudio" ? " (efectivo)" : ""}`,
        v.cubre === "floor" ? "suelo" : v.cubre === "ride" ? "spinning" : "ilimitado",
        v.email,
        v.precio,
      ]);
    // Punto y coma: es lo que espera Excel en español, y así no parte los decimales.
    const csv = filas.map((f) => f.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(";")).join("\n");
    const url = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `apexride-ingresos-${datos.desde}-a-${datos.hasta}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const Bloque = ({ titulo, d, destacado }) => (
    <div
      className="border p-5"
      style={{ ...borde, background: destacado ? `${GOLD}0D` : "#111", borderColor: destacado ? `${GOLD}66` : `${GOLD}33` }}
    >
      <p className="text-[10px] uppercase tracking-[0.3em]" style={{ color: destacado ? GOLD : "#ffffff66" }}>
        {titulo}
      </p>
      <p className="mt-3 text-3xl font-bold text-white">{dinero(d.neto)}</p>
      <p className="text-[11px] text-white/40">neto, después de comisión</p>

      <div className="mt-4 pt-3 space-y-1.5 text-sm border-t" style={{ borderColor: "#ffffff11" }}>
        <div className="flex justify-between text-white/70">
          <span>Clases sueltas ({d.clases.cobros})</span>
          <span>{dinero(d.clases.bruto)}</span>
        </div>
        <div className="flex justify-between text-white/70">
          <span>Paquetes ({d.paquetes.ventas})</span>
          <span>{dinero(d.paquetes.bruto)}</span>
        </div>
        <div className="flex justify-between text-white pt-1.5 border-t" style={{ borderColor: "#ffffff11" }}>
          <span>Bruto</span>
          <span>{dinero(d.bruto)}</span>
        </div>
        <div className="flex justify-between text-red-300/80">
          <span>Comisión estimada</span>
          <span>−{dinero(d.comision_estimada)}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-5 py-6" data-testid="admin-dinero">
      <div className="flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="block mb-1 text-[10px] uppercase tracking-[0.2em] text-white/40">Desde</span>
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="h-10 bg-black/60 border px-3 text-white outline-none text-sm" style={borde} />
        </label>
        <label className="block">
          <span className="block mb-1 text-[10px] uppercase tracking-[0.2em] text-white/40">Hasta</span>
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="h-10 bg-black/60 border px-3 text-white outline-none text-sm" style={borde} />
        </label>
        <Boton onClick={cargar}>{cargando ? "Calculando…" : "Actualizar"}</Boton>
        <Boton tono="oro" onClick={bajarExcel} disabled={!datos}>
          Bajar a Excel
        </Boton>
      </div>

      {!datos ? (
        <p className="mt-10 text-sm text-white/40">{cargando ? "Calculando…" : "Sin datos en ese periodo."}</p>
      ) : (
        <>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            <Bloque titulo="Aeróbicos y suelo" d={datos.suelo} destacado />
            <Bloque titulo="Spinning" d={datos.spinning} />
            <div className="border p-5" style={{ ...borde, background: "#111" }}>
              <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">Todo junto</p>
              <p className="mt-3 text-3xl font-bold" style={{ color: GOLD }}>
                {dinero(datos.total.neto)}
              </p>
              <p className="text-[11px] text-white/40">neto del periodo</p>
              <div className="mt-4 pt-3 space-y-1.5 text-sm border-t" style={{ borderColor: "#ffffff11" }}>
                <div className="flex justify-between text-white/70">
                  <span>Bruto</span>
                  <span>{dinero(datos.total.bruto)}</span>
                </div>
                <div className="flex justify-between text-red-300/80">
                  <span>Comisión ({datos.comision.pct}% + {dinero(datos.comision.fija)})</span>
                  <span>−{dinero(datos.total.comision_estimada)}</span>
                </div>
                <div className="flex justify-between text-white/40">
                  <span>Cobros</span>
                  <span>{datos.cobros}</span>
                </div>
              </div>
            </div>
          </div>

          {datos.ilimitado_sin_repartir > 0 && (
            <div className="mt-5 border px-5 py-4" style={{ borderColor: `${GOLD}55`, background: `${GOLD}0D` }}>
              <p className="text-sm" style={{ color: GOLD }}>
                {dinero(datos.ilimitado_sin_repartir)} de paquetes Ilimitado sin repartir
              </p>
              <p className="mt-1 text-xs text-white/50">
                El Ilimitado sirve para las dos disciplinas. Se reparte según las clases que la persona use;
                en cuanto asista a la primera, este importe se coloca solo.
              </p>
            </div>
          )}

          <div className="mt-5 border px-5 py-4" style={{ ...borde, background: "#111" }}>
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">Clases dadas con paquete</p>
            <p className="mt-2 text-sm text-white/70">
              Suelo: <b className="text-white">{datos.clases_cubiertas_por_paquete.floor}</b> &nbsp;·&nbsp;
              Spinning: <b className="text-white">{datos.clases_cubiertas_por_paquete.ride}</b>
            </p>
            <p className="mt-1 text-xs text-white/40">
              No entró dinero esos días — se cobró al vender el paquete —, pero la coach sí las impartió.
            </p>
          </div>

          {datos.ventas_paquete.length > 0 && (
            <div className="mt-5">
              <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Paquetes vendidos</p>
              <div className="border divide-y" style={{ ...borde, borderColor: "#ffffff11" }}>
                {datos.ventas_paquete.map((v, i) => (
                  <div key={i} className="px-4 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                    <span className="text-white/50">{v.fecha.slice(0, 10)}</span>
                    <span className="text-white font-semibold">{v.paquete}</span>
                    <span className="text-white/50">{v.email}</span>
                    {v.origen === "estudio" && (
                      <span className="text-[10px] uppercase px-2 py-0.5 bg-emerald-900/50 text-emerald-300">Efectivo</span>
                    )}
                    <span className="ml-auto" style={{ color: GOLD }}>
                      {dinero(v.precio)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="mt-6 text-xs text-white/40 leading-relaxed">
            La comisión es una estimación con la tarifa estándar de Stripe ({datos.comision.pct}% + {dinero(datos.comision.fija)}
            por cobro). La cifra exacta está en Stripe, en Balance. Los paquetes pagados en efectivo no llevan comisión.
            Se cuenta por fecha de cobro, no por fecha de clase.
          </p>
        </>
      )}
    </div>
  );
}

/**
 * Los códigos de descuento. El estudio reparte algunos a entrenadores para
 * que le recomienden clientes, así que lo que importa es ver cuánto trae cada
 * uno — si no, no hay forma de saber a quién le sale a cuenta seguir dándoselo.
 */
function Cupones({ api, onHecho, onError }) {
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [nuevo, setNuevo] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await api("get", "/admin/cupones");
      setLista(r.cupones || []);
    } catch (e) {
      onError(e.response?.data?.detail || "No se pudieron cargar.");
    } finally {
      setCargando(false);
    }
  }, [api, onError]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const hacer = async (fn, mensaje) => {
    try {
      await fn();
      if (mensaje) onHecho(mensaje);
      await cargar();
      return true;
    } catch (e) {
      onError(e.response?.data?.detail || "No se pudo.");
      return false;
    }
  };

  const borrar = async (c) => {
    try {
      const r = await api("delete", `/admin/cupones/${c.code}`);
      onHecho(r.apagado ? `${c.code} se apagó (ya se había usado ${r.veces} veces, se conserva el historial).` : `${c.code} borrado.`);
      await cargar();
    } catch (e) {
      onError(e.response?.data?.detail || "No se pudo.");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 py-6" data-testid="admin-cupones">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-white/60 max-w-xl">
          Los códigos marcados como <b className="text-white">privados</b> no salen en la web: se reparten a mano.
          Los <b className="text-white">públicos</b> se anuncian en la página de precios.
        </p>
        <Boton tono="oro" onClick={() => setNuevo(true)} data-testid="admin-new-coupon">
          + Código
        </Boton>
      </div>

      {cargando ? (
        <p className="mt-8 text-sm text-white/40">Cargando…</p>
      ) : (
        <div className="mt-6 space-y-3">
          {lista.map((c) => (
            <div
              key={c.code}
              className="border p-4"
              style={{ ...borde, background: "#111", opacity: c.active ? 1 : 0.45 }}
              data-testid={`admin-coupon-${c.code}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-bold text-lg tracking-[0.1em]" style={{ color: GOLD }}>
                    {c.code}
                  </span>
                  <span className="text-white font-semibold">{c.discount_pct}%</span>
                  {c.label && <span className="text-white/50 text-sm">· {c.label}</span>}
                  {c.publico ? (
                    <span className="text-[10px] uppercase px-2 py-0.5" style={{ background: `${GOLD}22`, color: GOLD }}>
                      En la web
                    </span>
                  ) : (
                    <span className="text-[10px] uppercase px-2 py-0.5 bg-white/10 text-white/50">Privado</span>
                  )}
                  {c.uso === "una_vez" && (
                    <span className="text-[10px] uppercase px-2 py-0.5 bg-sky-900/50 text-sky-300">Una vez por cliente</span>
                  )}
                  {c.alcance === "una_clase" && (
                    <span className="text-[10px] uppercase px-2 py-0.5 bg-white/10 text-white/50">Solo una clase</span>
                  )}
                  {!c.active && <span className="text-[10px] uppercase px-2 py-0.5 bg-red-900/50 text-red-300">Apagado</span>}
                </div>

                <div className="flex items-center gap-2">
                  <Boton onClick={() => hacer(() => api("patch", `/admin/cupones/${c.code}`, { active: !c.active }), c.active ? `${c.code} apagado.` : `${c.code} encendido.`)}>
                    {c.active ? "Apagar" : "Encender"}
                  </Boton>
                  <Boton onClick={() => hacer(() => api("patch", `/admin/cupones/${c.code}`, { publico: !c.publico }), c.publico ? `${c.code} ya no sale en la web.` : `${c.code} ahora sale en la web.`)}>
                    {c.publico ? "Ocultar" : "Publicar"}
                  </Boton>
                  <Boton tono="rojo" onClick={() => borrar(c)}>
                    Borrar
                  </Boton>
                </div>
              </div>

              <div className="mt-3 pt-3 border-t flex flex-wrap gap-x-8 gap-y-1 text-sm" style={{ borderColor: "#ffffff11" }}>
                <span className="text-white/50">
                  <b className="text-white">{c.veces}</b> {c.veces === 1 ? "compra" : "compras"}
                </span>
                <span className="text-white/50">
                  <b className="text-white">{c.clientes}</b> {c.clientes === 1 ? "cliente" : "clientes"}
                </span>
                <span className="text-white/50">
                  Trajo <b style={{ color: GOLD }}>${Number(c.cobrado).toFixed(2)}</b>
                </span>
                <span className="text-white/50">
                  Descontado <b className="text-white/70">${Number(c.descontado).toFixed(2)}</b>
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {nuevo && (
        <Ventana titulo="Código nuevo" onClose={() => setNuevo(false)}>
          <FormCupon
            onGuardar={async (cuerpo) => {
              const ok = await hacer(() => api("post", "/admin/cupones", cuerpo), `Código ${cuerpo.code} creado.`);
              if (ok) setNuevo(false);
              return ok;
            }}
          />
        </Ventana>
      )}
    </div>
  );
}

function FormCupon({ onGuardar }) {
  const [f, setF] = useState({ code: "", discount_pct: "25", label: "", uso: "siempre", publico: false });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const enviar = async (e) => {
    e.preventDefault();
    setBusy(true);
    await onGuardar({
      code: f.code,
      discount_pct: Number(f.discount_pct),
      label: f.label,
      uso: f.uso,
      alcance: "compra",
      publico: f.publico,
    });
    setBusy(false);
  };

  return (
    <form onSubmit={enviar} className="space-y-4">
      <Campo
        etiqueta="Código"
        value={f.code}
        onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") })}
        placeholder="ALEXA25"
        required
        autoFocus
      />
      <div className="grid grid-cols-2 gap-3">
        <Campo etiqueta="Descuento (%)" type="number" min="1" max="100" value={f.discount_pct} onChange={set("discount_pct")} required />
        <Campo etiqueta="Para quién (opcional)" value={f.label} onChange={set("label")} placeholder="Entrenador Alexa" />
      </div>

      <div>
        <span className="block mb-1 text-[10px] uppercase tracking-[0.2em] text-white/40">Cuántas veces lo puede usar un cliente</span>
        <div className="flex gap-2">
          {[
            ["siempre", "Las que quiera"],
            ["una_vez", "Una sola vez"],
          ].map(([id, texto]) => (
            <button
              key={id}
              type="button"
              onClick={() => setF({ ...f, uso: id })}
              className="flex-1 py-2.5 text-sm"
              style={f.uso === id ? { background: GOLD, color: "#0C0C0C", fontWeight: 700 } : { border: `1px solid ${GOLD}44`, color: "#fff" }}
            >
              {texto}
            </button>
          ))}
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer select-none">
        <input type="checkbox" checked={f.publico} onChange={(e) => setF({ ...f, publico: e.target.checked })} className="accent-[#C5A059]" />
        Anunciarlo en la página de precios
      </label>
      <p className="text-xs text-white/40">
        Si lo dejas sin marcar, el código no aparece en ningún sitio: solo lo tiene quien tú se lo des.
      </p>

      <div className="flex justify-end pt-2">
        <Boton tono="oro" type="submit" disabled={busy}>
          {busy ? "Creando…" : "Crear código"}
        </Boton>
      </div>
    </form>
  );
}

function Clientes({ api, paquetes, onHecho, onError }) {
  const [q, setQ] = useState("");
  const [lista, setLista] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [abierto, setAbierto] = useState("");

  const buscar = useCallback(
    async (texto) => {
      if (texto.trim().length < 2) return setLista([]);
      setBuscando(true);
      try {
        const r = await api("get", `/admin/customers?q=${encodeURIComponent(texto.trim())}`);
        setLista(r.customers || []);
      } catch (e) {
        onError(e.response?.data?.detail || "No se pudo buscar.");
      } finally {
        setBuscando(false);
      }
    },
    [api, onError]
  );

  // Busca sola mientras escribe, sin castigar al servidor con cada tecla.
  useEffect(() => {
    const t = setTimeout(() => buscar(q), 350);
    return () => clearTimeout(t);
  }, [q, buscar]);

  const acreditar = async (email, cuerpo, mensaje) => {
    try {
      await api("post", "/admin/customers/grant", { email, ...cuerpo });
      onHecho(mensaje);
      buscar(q);
    } catch (e) {
      onError(e.response?.data?.detail || "No se pudo acreditar.");
    }
  };

  const quitar = async (email) => {
    try {
      await api("post", "/admin/customers/revoke", { email });
      onHecho("Paquete retirado.");
      buscar(q);
    } catch (e) {
      onError(e.response?.data?.detail || "No se pudo retirar.");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 py-6">
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Nombre o correo del cliente…"
        data-testid="admin-customer-search"
        className="h-11 w-full bg-black/60 border px-4 text-white outline-none"
        style={borde}
      />
      <p className="mt-2 text-xs text-white/40">
        Aquí se acredita lo que se pagó en el estudio: un paquete en efectivo, o una clase de regalo.
      </p>

      {buscando && <p className="mt-6 text-sm text-white/40">Buscando…</p>}
      {!buscando && q.trim().length >= 2 && lista.length === 0 && (
        <p className="mt-6 text-sm text-white/40">Nadie con ese nombre o correo. Tiene que tener cuenta en la web.</p>
      )}

      <div className="mt-5 space-y-3">
        {lista.map((cl) => {
          const m = cl.membership;
          const activo = m && m.active;
          return (
            <div key={cl.email} className="border p-4" style={{ ...borde, background: "#111" }} data-testid={`admin-customer-${cl.email}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-white font-semibold">{cl.name}</p>
                  <p className="text-sm text-white/50">
                    <a href={`mailto:${cl.email}`} className="hover:text-white">
                      {cl.email}
                    </a>
                    {cl.phone && (
                      <>
                        {" · "}
                        <a href={`tel:${cl.phone}`} className="hover:text-white">
                          {cl.phone}
                        </a>
                      </>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs px-2.5 py-1" style={{ background: `${GOLD}22`, color: GOLD }}>
                    {cl.classes_booked} clases
                  </span>
                  {cl.free_credits > 0 && (
                    <span className="text-xs px-2.5 py-1 bg-emerald-900/50 text-emerald-300">{cl.free_credits} gratis</span>
                  )}
                  {activo && (
                    <span className="text-xs px-2.5 py-1 bg-sky-900/50 text-sky-300">
                      {m.unlimited ? "Ilimitado" : `${m.classes_remaining} de ${m.classes_total}`}
                    </span>
                  )}
                  <Boton onClick={() => setAbierto(abierto === cl.email ? "" : cl.email)}>
                    {abierto === cl.email ? "Cerrar" : "Acreditar"}
                  </Boton>
                </div>
              </div>

              {activo && (
                <p className="mt-2 text-xs text-white/40">
                  {m.name?.es || m.name?.en} · vence el {new Date(m.expires_at).toLocaleDateString("es-ES")}
                </p>
              )}

              {abierto === cl.email && (
                <div className="mt-4 pt-4 border-t" style={{ borderColor: "#ffffff11" }}>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">Paquete pagado en el estudio</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {paquetes.map((p) => (
                      <Boton
                        key={p.id}
                        onClick={() =>
                          acreditar(cl.email, { package_id: p.id }, `Paquete acreditado a ${cl.name}.`)
                        }
                        data-testid={`admin-grant-${p.id}`}
                      >
                        {p.name.es}
                      </Boton>
                    ))}
                  </div>

                  <p className="mt-5 text-[10px] uppercase tracking-[0.2em] text-white/40">Clases de regalo</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {[0, 1, 2, 3].map((n) => (
                      <Boton
                        key={n}
                        tono={cl.free_credits === n ? "oro" : "normal"}
                        onClick={() => acreditar(cl.email, { free_credits: n }, `${cl.name} tiene ahora ${n} clase(s) de regalo.`)}
                      >
                        {n}
                      </Boton>
                    ))}
                  </div>

                  {activo && (
                    <div className="mt-5">
                      <Boton tono="rojo" onClick={() => quitar(cl.email)}>
                        Quitar el paquete
                      </Boton>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
